import type { AdminCustomerCart } from "@hobbyshopify/backend-client";
import { useEffect, useRef, useState } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { AppPrompt } from "../../components/AppPrompt";
import { formatCents } from "../../core/primitives/card-display";
import {
  cartLineImage,
  cartLineSubtitle,
  cartLinesTotalCents,
  cartLineTitle,
  lineStockNote,
} from "../../core/primitives/customer-cart";
import { customerLabel } from "../../core/primitives/customer-label";
import { formatAmount } from "../../core/primitives/money";
import { type CartItem, CustomerCartActions, CustomerCartState } from "./customer-cart-controller";
import { TerminalPaymentModal } from "./terminal-payment-modal";

type CartStatus = AdminCustomerCart["status"];

// Open is the cart that still needs the merchant; paid is settled and locked.
// So open takes the accent and paid recedes — the opposite of what "done is
// green" would suggest, because these tiles exist to surface outstanding work.
const CART_STATUS_STYLES: Record<CartStatus, string> = {
  OPEN: "badge-accent",
  PAID: "badge-neutral",
  // Nobody came back to it, so the shop stopped holding the cards and released
  // the copies. Warn rather than neutral: it is the one state where the cards
  // in the list below are no longer reserved for this customer.
  EXPIRED: "badge-warn",
};

/**
 * How long the shop is still holding this cart.
 *
 * The same deadline the customer is watching, read from the same field, so the
 * two screens cannot tell different stories about when the cards go back on
 * sale. Adding a card or opening a payment pushes it out.
 */
function HoldRemaining({ expiresAt }: { expiresAt: Date | null }) {
  if (!expiresAt) return null;
  const minutes = Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 60_000);
  if (minutes <= 0) return null;
  return (
    <p className={`text-xs ${minutes <= 10 ? "text-warn" : "text-faint"}`}>
      Held for {minutes === 1 ? "less than a minute" : `${minutes} more minutes`}
    </p>
  );
}

function titleCase(s: string): string {
  return s.charAt(0) + s.slice(1).toLowerCase();
}

function Badge({ status, cls }: { status: string; cls: string }) {
  return <span className={`badge ${cls}`}>{titleCase(status)}</span>;
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={1.75}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
    </svg>
  );
}

function ItemRow({
  item,
  cartStatus,
  busy,
  readOnly,
  canDelete,
  onToggleInclude,
  onDelete,
}: {
  item: CartItem;
  cartStatus: CartStatus;
  busy: boolean;
  readOnly: boolean;
  canDelete: boolean;
  onToggleInclude: (item: CartItem) => void;
  onDelete: (item: CartItem) => void;
}) {
  const excluded = !item.includedInPurchaseOrder;
  const title = cartLineTitle(item);
  const subtitle = cartLineSubtitle(item);
  const imageUrl = cartLineImage(item);
  // Seen here above all: this is the moment somebody hands the item over.
  const stockNote = lineStockNote(item, cartStatus);
  return (
    <div className="flex flex-col gap-2 rounded-btn border border-line p-2.5">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-thumb bg-surface-strong">
          {imageUrl ? (
            <img src={imageUrl} alt={title} className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-[10px] text-faint">No img</div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className={`truncate text-sm ${excluded ? "text-faint line-through" : "text-white"}`}>{title}</p>
          {subtitle && <p className="truncate text-xs text-faint">{subtitle}</p>}
        </div>
        {excluded && <Badge status="Excluded" cls="badge-neutral" />}
        {stockNote && <span className="badge badge-warn">{stockNote}</span>}
        {/* Dimmed rather than accented when excluded — it is not part of the
            total the merchant is about to charge. */}
        <span className={`w-16 shrink-0 text-right text-sm font-medium ${excluded ? "text-faint" : "text-accent"}`}>
          {item.quantity > 1 && <span className="mr-1 text-faint">{item.quantity}×</span>}
          {formatCents(item.offer.priceCents)}
        </span>
      </div>

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          {/* Held, not disabled, while a change is read back: a disabled control drops the focus it had. */}
          <label className="flex select-none items-center gap-1 text-xs text-muted">
            <input
              type="checkbox"
              checked={item.includedInPurchaseOrder}
              aria-disabled={busy || undefined}
              aria-label={`In order: ${title}`}
              onChange={() => {
                if (!busy) onToggleInclude(item);
              }}
              className="accent-accent aria-disabled:opacity-50"
            />
            In order
          </label>
          {canDelete && (
            <button
              type="button"
              onClick={() => {
                if (!busy) onDelete(item);
              }}
              aria-disabled={busy || undefined}
              className="ml-auto shrink-0 p-1 text-faint transition-colors hover:text-danger aria-disabled:opacity-40"
              aria-label={`Remove ${title}`}
              title="Remove item"
            >
              <CloseIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** One customer cart: its lines, its total, payment at the counter, the checkout link, and deleting it. */
export function CustomerCartTile({ cart }: { cart: AdminCustomerCart }) {
  const state = useSubtree(CustomerCartState);
  const actions = useSubtree(CustomerCartActions);
  const observer = useObserver();
  const busyCartId = observer.watch(state.busyCartId);
  const busyCart = busyCartId === cart.id;
  // One change at a time, across every cart shown: the rest waits while a change is read back.
  const changing = observer.watch(state.busyItemId) !== null || busyCartId !== null;
  const confirmingDelete = observer.watch(state.confirmDeleteId) === cart.id;
  const deleteError = observer.watch(state.deleteError);
  // Delete cart and its question take each other's place: the focus moves with them, to the question's safe
  // answer, and back to Delete cart when the question goes with the focus in it.
  const keep = useRef<HTMLButtonElement>(null);
  const deleteCart = useRef<HTMLButtonElement>(null);
  const asked = useRef(false);
  useEffect(() => {
    if (confirmingDelete) keep.current?.focus();
    else if (asked.current && (document.activeElement === null || document.activeElement === document.body))
      // Where the question was, and without scrolling: what the sheet says at its top keeps its place in view.
      deleteCart.current?.focus({ preventScroll: true });
    asked.current = confirmingDelete;
  }, [confirmingDelete]);
  // A lapsed cart is as settled as a paid one from the merchant's side: its
  // copies are back on sale, so editing it would be editing a set of cards the
  // shop is no longer holding.
  const readOnly = cart.status !== "OPEN";
  const canDeleteItem = !readOnly && cart.items.length > 1;
  const totalCents = cartLinesTotalCents(cart);
  // Only once tax has actually been quoted onto a checkout. A checkout with no
  // tax on it falls back to the plain total: a shop that charges none has
  // nothing to break out, and a "Sales tax $0.00" line invites the question.
  const checkout = cart.checkout;
  const quote =
    checkout && checkout.taxAmount !== null && checkout.subtotalAmount !== null && checkout.amountTotal !== null
      ? { ...checkout, taxAmount: checkout.taxAmount }
      : null;
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    if (!cart.checkoutUrl) {
      return;
    }
    await navigator.clipboard.writeText(cart.checkoutUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="panel relative flex min-w-0 flex-col p-4 sm:p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{customerLabel(cart.user)}</p>
          {cart.status === "OPEN" ? (
            <HoldRemaining expiresAt={cart.expiresAt} />
          ) : (
            cart.status === "EXPIRED" && (
              <p className="text-xs text-warn">The hold ran out — these copies are back on sale.</p>
            )
          )}
        </div>
        <Badge status={cart.status} cls={CART_STATUS_STYLES[cart.status]} />
      </div>

      <div className="mb-4 space-y-2">
        {cart.items.map((item) => (
          <ItemRow
            key={item.id}
            item={item}
            cartStatus={cart.status}
            busy={confirmingDelete || changing}
            readOnly={readOnly}
            canDelete={canDeleteItem}
            onToggleInclude={(it) => void actions.toggleInclude(cart, it)}
            onDelete={(it) => void actions.removeItem(cart, it)}
          />
        ))}
      </div>

      <div className="mb-4 border-t border-line pt-3">
        {quote ? (
          <>
            {/* The figures Stripe actually quoted, read off the checkout.
                They cannot describe a different cart than the one above:
                editing items cancels the checkout, and only a PENDING or
                PAID one reaches this DTO. */}
            <div className="flex justify-between py-0.5">
              <span className="text-xs text-faint">Items</span>
              <span className="text-xs text-muted">{formatAmount(quote.subtotalAmount, quote.currency)}</span>
            </div>
            <div className="flex justify-between py-0.5">
              <span className="text-xs text-faint">Sales tax</span>
              <span className="text-xs text-muted">{formatAmount(quote.taxAmount, quote.currency)}</span>
            </div>
            {/* The customer's fees, each on its own line and never summed into
                one: the shop reconciles the technology fee against HobbyScan
                and the processing fee against Stripe's deduction. */}
            {quote.platformFeeAmount !== null && (
              <div className="flex justify-between py-0.5">
                <span className="text-xs text-faint">HobbyScan technology fee (paid by customer)</span>
                <span className="text-xs text-muted">{formatAmount(quote.platformFeeAmount, quote.currency)}</span>
              </div>
            )}
            {quote.processingFeeAmount !== null && (
              <div className="flex justify-between py-0.5">
                <span className="text-xs text-faint">Card processing fee (paid by customer)</span>
                <span className="text-xs text-muted">{formatAmount(quote.processingFeeAmount, quote.currency)}</span>
              </div>
            )}
            <div className="mt-1.5 flex justify-between">
              <span className="text-sm font-semibold text-muted">Purchase order total</span>
              <span className="text-base font-bold text-accent">{formatAmount(quote.amountTotal, quote.currency)}</span>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between">
              <span className="text-sm font-semibold text-muted">Purchase order total</span>
              <span className="text-base font-bold text-accent">{formatCents(totalCents)}</span>
            </div>
            {/* No checkout yet, so there is no tax figure to show — quoting one
                per card on this list would bill the shop a Stripe Tax
                calculation for rendering a page. Saying it is coming is what
                stops the total above reading as the final word. */}
            {cart.salesTaxAtCheckout && <p className="mt-1 text-xs text-faint">Sales tax is added when they pay.</p>}
          </>
        )}
      </div>

      {!readOnly && (
        <button
          type="button"
          onClick={() => actions.takePayment(cart)}
          disabled={confirmingDelete || changing}
          className="btn-primary btn-xs mb-2 w-full"
        >
          Take payment in person
        </button>
      )}
      {!readOnly && (
        <div className="mb-2">
          <AppPrompt>Tap to Pay takes a card on your phone, in the HobbyScan app.</AppPrompt>
        </div>
      )}

      {cart.checkoutUrl && !readOnly ? (
        <button
          type="button"
          onClick={handleCopyLink}
          disabled={busyCart || confirmingDelete}
          className="btn-chip w-full justify-center truncate"
        >
          {copied ? "Link copied" : "Copy checkout link"}
        </button>
      ) : null}

      {cart.status === "PAID" && <p className="mt-2 text-center text-xs text-faint">Paid — this cart is locked.</p>}

      {!readOnly && (
        <div className="mt-3 border-t border-line pt-3">
          {confirmingDelete ? (
            <div className="note note-danger p-3">
              <p className="text-sm font-semibold text-danger">Delete this cart?</p>
              <p className="mt-1 text-xs leading-5 text-muted">
                This removes every item and deletes the draft order behind it. This cannot be undone.
              </p>
              {deleteError?.id === cart.id && (
                <p role="alert" className="mt-2 text-xs font-medium text-danger">
                  {deleteError.message}
                </p>
              )}
              <div className="mt-3 flex gap-2">
                <button
                  ref={keep}
                  type="button"
                  onClick={() => {
                    if (!busyCart) actions.cancelDelete();
                  }}
                  aria-disabled={busyCart || undefined}
                  className="btn-secondary btn-xs flex-1 aria-disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!busyCart) void actions.confirmDelete(cart);
                  }}
                  aria-disabled={busyCart || undefined}
                  className="btn-destructive btn-xs flex-1 aria-disabled:opacity-50"
                >
                  {busyCart ? "Deleting…" : "Delete"}
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              ref={deleteCart}
              onClick={() => {
                if (!changing) actions.promptDelete(cart);
              }}
              aria-disabled={changing || undefined}
              className="btn-ghost hover:text-danger aria-disabled:opacity-50"
            >
              <TrashIcon />
              Delete cart
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** The card reader, open over the page for the cart payment was asked for. */
export function CartPayment() {
  const state = useSubtree(CustomerCartState);
  const observer = useObserver();
  const paying = observer.watch(state.payingCart);
  if (!paying) return null;
  return <TerminalPaymentModal customerCartId={paying.id} fallbackTotalCents={cartLinesTotalCents(paying)} />;
}
